// PanelCanvas2D.jsx
import React, { useMemo, memo, useState, useCallback, useEffect, useRef } from "react";
import { Stage, Layer, Line, Arrow, Text } from "react-konva";
import CustomCabinet from "./CustomCabinet";
import CustomSections from "./CustomSections";
import Legend from "./Legend";
import InfoBox from "./InfoBox";
import "./PanelCanvas2D.css";
import { ENCLOSURE_ID, selectionIds } from "../../../Layouts/LayoutContext";

// Space the dimension lines + labels need right of / below the montage plate
// (drawn at +40 / +55 / +80 mm with 32 px text), in drawing units (mm).
const DIM_SPACE_RIGHT = 120;
const DIM_SPACE_BOTTOM = 100;

// Margin around the drawing when fitting, in screen pixels
const FIT_MARGIN_PX = 24;

function PanelCanvas2D({
  snapshot,
  version,
  optimized,
  sectionPlacements,
  sections,
  projectId,
  devices = [],
  hiddenIds,     // Set<string> — same ids used by the tree and the 3D view
  selectedId,
  onSelect,      // (id | null) => void
}) {
  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [showLegend, setShowLegend] = useState(false);
  const [hoveredDeviceInfo, setHoveredDeviceInfo] = useState(null);
  const scaleBy = 1.1;

  // ── Canvas size = size of the .Stage box ────────────────────────────────
  // Callback ref (state): the box only exists once there is a cabinet to
  // draw, so the observer has to start when the element appears, not on mount.
  const [containerEl, setContainerEl] = useState(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerEl) return;
    let frame = 0;

    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = containerEl.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        if (width <= 0 || height <= 0) return; // hidden tab / collapsed panel
        setViewport((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height });
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(containerEl);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [containerEl]);

  // ── Zoom / pan ──────────────────────────────────────────────────────────
  // Once the user zooms or pans, a resize keeps their view; the Fit button
  // (or a different cabinet) returns to automatic fitting.
  const userMovedRef = useRef(false);

  const handleWheel = useCallback(
    (e) => {
      e.evt.preventDefault();
      userMovedRef.current = true;
      const stage = e.target.getStage();
      const oldScale = stageScale;
      const pointer = stage.getPointerPosition();

      const mousePointTo = {
        x: (pointer.x - stagePosition.x) / oldScale,
        y: (pointer.y - stagePosition.y) / oldScale,
      };

      const direction = e.evt.deltaY > 0 ? 1 : -1;
      const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

      setStageScale(newScale);
      setStagePosition({
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      });
    },
    [stageScale, stagePosition]
  );

  const handleDragEnd = (e) => {
    // Only the stage itself is draggable; ignore drag events bubbling from shapes
    if (e.target !== e.target.getStage()) return;
    userMovedRef.current = true;
    setStagePosition(e.target.position());
  };

  // Click on empty stage space (not on a device shape) clears selection —
  // same "click empty space to deselect" behavior as the 3D view.
  const handleStageMouseDown = (e) => {
    if (e.target === e.target.getStage()) onSelect?.(null);
  };

  const cabWidth = snapshot?.cabinet?.size?.width ?? 0;
  const cabHeight = snapshot?.cabinet?.size?.height ?? 0;

  // Fit cabinet + dimension lines into the canvas, centred
  const fitToScreen = useCallback(() => {
    if (!cabWidth || !cabHeight || !viewport.width || !viewport.height) return;

    const contentW = cabWidth + DIM_SPACE_RIGHT;
    const contentH = cabHeight + DIM_SPACE_BOTTOM;

    const fitScale = Math.max(0.01, Math.min(
      (viewport.width - FIT_MARGIN_PX * 2) / contentW,
      (viewport.height - FIT_MARGIN_PX * 2) / contentH
    ));

    setStageScale(fitScale);
    setStagePosition({
      x: (viewport.width - contentW * fitScale) / 2,
      y: (viewport.height - contentH * fitScale) / 2,
    });
  }, [cabWidth, cabHeight, viewport]);

  // Fit button: back to automatic fitting
  const handleFitClick = () => {
    userMovedRef.current = false;
    fitToScreen();
  };

  // Auto-fit: always for a different cabinet; on resize only while the
  // user hasn't zoomed/panned.
  const lastCabinetRef = useRef("");
  useEffect(() => {
    const key = `${cabWidth}x${cabHeight}`;
    if (key !== lastCabinetRef.current) {
      lastCabinetRef.current = key;
      userMovedRef.current = false;
    }
    if (!userMovedRef.current) fitToScreen();
  }, [fitToScreen, cabWidth, cabHeight]);

  // Everything that is selected: one device, or all devices of a selected section
  const selectedIds = useMemo(
    () => selectionIds(selectedId, sectionPlacements),
    [selectedId, sectionPlacements]
  );

  if (cabWidth <= 0 || cabHeight <= 0) return null;

  const {
    cabinet,
    montagePlate,
    ductLeft,
    ductRight,
    generatedSections,
    ductTop,
    ductTopEnclosure,
  } = snapshot;
  const offsetX = (cabinet?.size?.width - montagePlate?.size?.width) / 2;
  const offsetY = (cabinet?.size?.height - montagePlate?.size?.height) / 2;
  const cabinetX = 0;
  const cabinetY = 0;

  return (
    <div className="panel-canvas-2d-container">
      {/* Toolbar for 2D mode */}
      <div className="panel-canvas-toolbar-2d">
        {/* <label style={{ userSelect: "none" }}>
          <input
            type="checkbox"
            checked={showLegend}
            onChange={(e) => setShowLegend(e.target.checked)}
          />{" "}
          Show Legend & Section Borders
        </label> */}
        {/* {showLegend && (
          <>
            <Legend
              generatedSections={generatedSections}
              sectionPlacements={sectionPlacements}
              sections={sections}
              title="Section Legend"
            />
            <InfoBox info={hoveredDeviceInfo} />
          </>
        )} */}
      </div>
      <div className="panel-canvas-2d-row">
        {/* Fit button: inside the canvas frame (the row's own box), top right */}
        <button
          type="button"
          onClick={handleFitClick}
          title="Fit to screen"
          className="fit-button-2d"
        >
          ⤢
        </button>

        {/* KONVA Canvas — sized to this box by the ResizeObserver above */}
        <div className="Stage" ref={setContainerEl}>
          {viewport.width > 0 && viewport.height > 0 && (
            <Stage
              width={viewport.width}
              height={viewport.height}
              scaleX={stageScale}
              scaleY={stageScale}
              x={stagePosition.x}
              y={stagePosition.y}
              draggable
              onWheel={handleWheel}
              onDragEnd={handleDragEnd}
              onMouseDown={handleStageMouseDown}
            >
              <Layer>
                {/* Cabinet outline - always drawn in 2D. The enclosure
                    hide toggle in the tree only applies to the 3D view. */}
                <CustomCabinet
                  x={cabinetX}
                  y={cabinetY}
                  width={cabinet?.size?.width ?? 0}
                  height={cabinet?.size?.height ?? 0}
                  montagePlate={montagePlate}
                  ductTopEnclosure={ductTopEnclosure}
                  ductLeft={ductLeft}
                  ductRight={ductRight}
                  selected={selectedId === ENCLOSURE_ID}
                  onClick={() => onSelect?.(ENCLOSURE_ID)}
                />

                <CustomSections
                  generatedSections={generatedSections}
                  ductTop={ductTop}
                  cabinetX={cabinetX}
                  cabinetY={cabinetY}
                  optimized={optimized}
                  sectionPlacements={sectionPlacements}
                  sections={sections}
                  showSectionBorders={showLegend}
                  projectId={projectId}
                  devices={devices}
                  onDeviceHover={setHoveredDeviceInfo}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onDeviceSelect={onSelect}
                  hiddenIds={hiddenIds}
                />

                {/* DIMENSIONS */}
                <>
                  <Line
                    points={[
                      cabinetX + offsetX,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0),
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                    ]}
                    stroke="black"
                  />
                  <Arrow
                    points={[
                      cabinetX + 20 + offsetX,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                      cabinetX + offsetX,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                    ]}
                    stroke="black"
                    fill="black"
                    pointerLength={10}
                    pointerWidth={10}
                  />
                  <Arrow
                    points={[
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) - 20,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0),
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 40,
                    ]}
                    stroke="black"
                    fill="black"
                    pointerLength={10}
                    pointerWidth={10}
                  />
                  <Text
                    x={cabinetX + offsetX + (montagePlate?.size?.width ?? 0) / 2 - 30}
                    y={cabinetY + offsetY + (montagePlate?.size?.height ?? 0) + 55}
                    text={`${montagePlate?.size?.width ?? 0} mm`}
                    fontSize={32}
                  />

                  <Line
                    points={[
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY,
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0),
                    ]}
                    stroke="black"
                  />
                  <Arrow
                    points={[
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY + 20,
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY,
                    ]}
                    stroke="black"
                    fill="black"
                    pointerLength={10}
                    pointerWidth={10}
                  />
                  <Arrow
                    points={[
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0) - 20,
                      cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 40,
                      cabinetY + offsetY + (montagePlate?.size?.height ?? 0),
                    ]}
                    stroke="black"
                    fill="black"
                    pointerLength={10}
                    pointerWidth={10}
                  />
                  <Text
                    x={cabinetX + offsetX + (montagePlate?.size?.width ?? 0) + 80}
                    y={cabinetY + offsetY + (montagePlate?.size?.height ?? 0) / 2 - 10}
                    text={`${montagePlate?.size?.height ?? 0} mm`}
                    fontSize={32}
                    rotation={90}
                  />
                </>
              </Layer>
            </Stage>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(PanelCanvas2D);
